import { useCallback, useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
  useTheme
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

import SmartUploadReviewRow from "./SmartUploadReviewRow";
import SmartUploadSourcePanel from "./SmartUploadSourcePanel";
import SmartUploadSlideoutPrompt from "./SmartUploadSlideoutPrompt";
import SmartUploadCsvReview from "./SmartUploadCsvReview";
import SmartUploadManualEntryDialog from "./SmartUploadManualEntryDialog";

import {
  buildClosedYears,
  chunkCommitEntries,
  isLineReady,
  lineKeyFor,
  newVendorKeyFor,
  readLine,
  summarizeGateFailures,
  vendorPrefillToPersist,
  withDerivedRequirements
} from "./reviewLineGates";
import {
  commitSmartUploadLines,
  dismissSmartUploadRow,
  retrySmartUploadDocumentFiling,
  subscribeToSmartUploadLines,
  subscribeToSmartUploadTask,
  subscribeToSmartUploadTaskData,
  updateSmartUploadLines
} from "../../../helpers/smartUpload";
import {
  REVIEW_STATES,
  isReviewDrivenCsvFamily
} from "../../../helpers/smartUploadLineRequirements";
import { useCachedDisplayData } from "../../../helpers/firebase";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";

const PHYSICAL_CONTEXT_TYPES = ["offices", "vehicles"];

const EXTRACTING_TASK_STATUSES = ["uploading", "running"];
const EXTRACTING_ROW_STATUSES = ["pending", "extracting"];

// commit-lines/update-lines only accept a task in ready_for_review or
// partially_reviewed, so the dialog is read-only while extraction is still
// running and once the batch is finished (where only Retry filing remains).
const reviewModeFor = (task, rows) => {
  if (
    EXTRACTING_TASK_STATUSES.includes(task.status) ||
    rows.some(({ status }) => EXTRACTING_ROW_STATUSES.includes(status))
  ) {
    return "reading";
  }
  return task.status === "finished" ? "finished" : "review";
};

const REVIEW_MODE_NOTICES = {
  reading:
    "Still reading your documents — you'll be able to review when it's done.",
  finished: "This batch is finished — every file has been reviewed."
};

const connectionErrorResult = ({ rowId, lineId }, message) => ({
  rowId,
  lineId,
  outcome: "error",
  message: message || "We couldn't reach the server — please try again."
});

const commitChunk = async ({
  companyId,
  taskDocId,
  chunk,
  confirmSlideoutPrompt
}) => {
  const response = await commitSmartUploadLines({
    companyId,
    taskDocId,
    lines: chunk.map(({ rowId, lineId }) => ({ rowId, lineId })),
    confirmSlideoutPrompt
  });
  if (!response || response.error || !Array.isArray(response.results)) {
    return chunk.map((entry) =>
      connectionErrorResult(entry, response?.message)
    );
  }
  return response.results;
};

// Sends ready lines in chunks (≤100 lines, ≤5 new vendor names each), one
// call at a time, then re-sends whatever the server deferred until every line
// has a real outcome -- deferred lines are never failed or dropped. A round
// that makes no progress stops and leaves those lines "deferred" on screen.
const commitInRounds = async ({ entries, onChunkDone, ...commitProps }) => {
  const results = await chunkCommitEntries(entries).reduce(
    async (previousResults, chunk) => {
      const collected = await previousResults;
      const chunkResults = await commitChunk({ chunk, ...commitProps });
      onChunkDone(
        chunkResults.filter(({ outcome }) => outcome !== "deferred").length
      );
      return [...collected, ...chunkResults];
    },
    Promise.resolve([])
  );

  const deferredKeys = results
    .filter(({ outcome }) => outcome === "deferred")
    .map(lineKeyFor);
  const deferredEntries = entries.filter((entry) =>
    deferredKeys.includes(lineKeyFor(entry))
  );
  if (!deferredEntries.length || deferredEntries.length === entries.length) {
    return results;
  }

  const settledResults = results.filter(
    ({ outcome }) => outcome !== "deferred"
  );
  const retriedResults = await commitInRounds({
    entries: deferredEntries,
    onChunkDone,
    ...commitProps
  });
  return [...settledResults, ...retriedResults];
};

const groupByRow = (entries) =>
  entries.reduce(
    (groups, entry) => ({
      ...groups,
      [entry.rowId]: [...(groups[entry.rowId] || []), entry]
    }),
    {}
  );

// Confirming accepts the Vendor field's pre-fill (the document's issuer), so
// it is saved as vendor_name_final before commit -- the server gate reads only
// persisted fields.
const persistVendorPrefills = async ({ companyId, taskDocId, entries }) => {
  const prefillEntries = entries.filter(({ line }) =>
    vendorPrefillToPersist(line)
  );
  return Promise.all(
    Object.entries(groupByRow(prefillEntries)).map(([rowId, rowEntries]) =>
      updateSmartUploadLines({
        companyId,
        taskDocId,
        rowId,
        updates: rowEntries.map(({ lineId, line }) => ({
          lineId,
          fields: { vendor_name_final: vendorPrefillToPersist(line) }
        }))
      })
    )
  );
};

// A transactions or shipping CSV can hold thousands of rows: its review
// (SmartUploadCsvReview) reads pages from the server, so the row and line
// listeners here only start once the task is known not to be one.
const useSmartUploadBatch = ({ companyId, taskDocId }) => {
  const [task, setTask] = useState(null);
  const [rows, setRows] = useState([]);
  const [linesByRow, setLinesByRow] = useState({});
  const shouldListenToRows =
    !!task && !isReviewDrivenCsvFamily(task.document_family);

  useEffect(() => {
    if (!companyId || !taskDocId) {
      return undefined;
    }
    return subscribeToSmartUploadTask({
      companyId,
      taskDocId,
      onUpdate: setTask
    });
  }, [companyId, taskDocId]);

  useEffect(() => {
    if (!companyId || !taskDocId || !shouldListenToRows) {
      return undefined;
    }
    return subscribeToSmartUploadTaskData({
      companyId,
      taskDocId,
      onUpdate: setRows
    });
  }, [companyId, taskDocId, shouldListenToRows]);

  const rowIdsKey = rows.map(({ id }) => id).join("|");

  useEffect(() => {
    if (!companyId || !taskDocId || !rowIdsKey) {
      return undefined;
    }
    const unsubscribers = rowIdsKey.split("|").map((rowId) =>
      subscribeToSmartUploadLines({
        companyId,
        taskDocId,
        rowId,
        onUpdate: (lines) =>
          setLinesByRow((current) => ({ ...current, [rowId]: lines }))
      })
    );
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, [companyId, taskDocId, rowIdsKey]);

  return { task, rows, linesByRow };
};

const buildEntityNames = (offices, vehicles) =>
  Object.fromEntries(
    [...offices, ...vehicles].map(({ id, name }) => [id, name])
  );

const sortRows = (rows) =>
  [...rows].sort(
    (first, second) => (first.taskNum || 0) - (second.taskNum || 0)
  );

const batchSummary = ({ rows, entries }) => {
  const readyCount = entries.filter(({ line }) => isLineReady(line)).length;
  const recordedCount = entries.filter(
    ({ line }) => line.review_state === REVIEW_STATES.committed
  ).length;
  return [
    `${rows.length} ${rows.length === 1 ? "file" : "files"}`,
    `${entries.length} ${entries.length === 1 ? "line" : "lines"}`,
    `${readyCount} ready`,
    `${recordedCount} recorded`
  ].join(" · ");
};

// The one review screen for every Smart Upload document family (Design
// Intent 3.3). A Dialog, never a route (Feature Map §7). Every edit persists
// through update-lines as it happens, so re-opening resumes exactly where the
// reviewer stopped; confirms go through commit-lines, one line at a time on
// the server, so one bad line never blocks or undoes the others.
const SmartUploadReviewDialog = ({ companyId, taskDocId, onClose }) => {
  const { palette } = useTheme();
  const { activateSnackbar } = usePlatformLayoutContext() || {};
  const { task, rows, linesByRow } = useSmartUploadBatch({
    companyId,
    taskDocId
  });

  const [offices] = useCachedDisplayData("offices", []);
  const [vehicles] = useCachedDisplayData("vehicles", [
    ["type", "==", "company"]
  ]);
  const [vendorRelationships] = useCachedDisplayData("vendor-relationships");
  const [accountingYears] = useCachedDisplayData("accounting");

  const [pendingLineKeys, setPendingLineKeys] = useState([]);
  const [commitResults, setCommitResults] = useState({});
  const [commitProgress, setCommitProgress] = useState(null);
  const [isSlideoutConfirmed, setIsSlideoutConfirmed] = useState(false);
  const [promptEntries, setPromptEntries] = useState(null);
  const [sourceView, setSourceView] = useState(null);
  const [manualEntryTarget, setManualEntryTarget] = useState(null);

  const onPendingChange = useCallback(
    (lineKey, isPending) =>
      setPendingLineKeys((current) =>
        isPending
          ? [...new Set([...current, lineKey])]
          : current.filter((key) => key !== lineKey)
      ),
    []
  );

  const entityNames = buildEntityNames(offices || [], vehicles || []);
  const vendorOptions = (vendorRelationships || [])
    .filter(({ vendorName }) => !!vendorName)
    .map(({ id, vendorName }) => ({ id, name: vendorName }));
  const closedYears = buildClosedYears(accountingYears || []);

  if (!task) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="xl">
        <DialogContent>
          <CircularProgress size={24} />
        </DialogContent>
      </Dialog>
    );
  }

  const {
    entity_context: entityContext,
    document_family: taskDocumentFamily,
    archived
  } = task;
  const isPhysicalContext = PHYSICAL_CONTEXT_TYPES.includes(
    entityContext?.entity_type
  );
  const contextEntityName =
    entityNames[entityContext?.entity_id] || "this location";
  const fixedVendorName =
    entityContext?.entity_type === "vendor-relationship"
      ? vendorOptions.find(({ id }) => id === entityContext.entity_id)?.name ||
        "This vendor"
      : null;
  if (!archived && isReviewDrivenCsvFamily(taskDocumentFamily)) {
    return (
      <SmartUploadCsvReview
        companyId={companyId}
        taskDocId={taskDocId}
        task={task}
        onClose={onClose}
        vendorOptions={vendorOptions}
        closedYears={closedYears}
        isPhysicalContext={isPhysicalContext}
        contextEntityName={contextEntityName}
      />
    );
  }

  const orderedRows = sortRows(rows);

  const entries = orderedRows.flatMap((row) =>
    (linesByRow[row.id] || []).map((line) => ({
      rowId: row.id,
      lineId: line.id,
      documentFamily: row.document_family,
      line: withDerivedRequirements(readLine(line), row.document_family)
    }))
  );
  const readyEntries = entries.filter(({ line }) => isLineReady(line));
  const gateReasons = summarizeGateFailures(entries);
  const blockingReasons = [
    ...gateReasons,
    ...(pendingLineKeys.length ? ["Saving your edits…"] : [])
  ];
  const isCommitting = !!commitProgress;

  const runCommit = async (commitEntries) => {
    setCommitProgress({ done: 0, total: commitEntries.length });
    await persistVendorPrefills({
      companyId,
      taskDocId,
      entries: commitEntries
    });
    const results = await commitInRounds({
      companyId,
      taskDocId,
      confirmSlideoutPrompt: isPhysicalContext,
      entries: commitEntries.map((entry) => ({
        ...entry,
        vendorKey: newVendorKeyFor(entry.line)
      })),
      onChunkDone: (count) =>
        setCommitProgress((current) => ({
          ...current,
          done: Math.min(current.total, current.done + count)
        }))
    });
    setCommitResults((current) => ({
      ...current,
      ...Object.fromEntries(
        results.map((result) => [lineKeyFor(result), result])
      )
    }));
    return setCommitProgress(null);
  };

  const requestCommit = (commitEntries) => {
    if (isPhysicalContext && !isSlideoutConfirmed) {
      return setPromptEntries(commitEntries);
    }
    return runCommit(commitEntries);
  };

  const confirmSlideoutPrompt = () => {
    setIsSlideoutConfirmed(true);
    const confirmedEntries = promptEntries;
    setPromptEntries(null);
    return runCommit(confirmedEntries);
  };

  // #42's "Continue with guided manual entry" CTA (a failed or no-activity
  // row) and the per-line "Enter manually" hand-off (a replaced line) both
  // open SmartUploadManualEntryDialog directly -- it owns its own slideout
  // confirmation inline (Edge Cases: "the dialog shows the same
  // confirmation... even when the entity was prefilled"), independently of
  // the one-time isSlideoutConfirmed flag "Confirm all" uses, since a manual
  // entry commits itself the moment it's saved and needs its own tick every
  // time.
  const requestManualEntry = ({ row, line: replacedLine }) =>
    setManualEntryTarget({ row, line: replacedLine });

  const retryFiling = async ({ rowId, lineId }) => {
    const response = await retrySmartUploadDocumentFiling({
      companyId,
      taskDocId,
      rowId,
      lineId
    });
    if (response?.error && activateSnackbar) {
      activateSnackbar({
        message: response.message || "We couldn't file that document yet.",
        alert: "error"
      });
    }
    return response;
  };

  const dismissRow = async ({ id: rowId }) => {
    const response = await dismissSmartUploadRow({
      companyId,
      taskDocId,
      rowId
    });
    if (response?.error && activateSnackbar) {
      activateSnackbar({
        message: response.message || "We couldn't dismiss that file.",
        alert: "error"
      });
    }
    return response;
  };

  const reviewMode = reviewModeFor(task, rows);

  const renderBody = () => {
    if (archived) {
      return <Typography variant="body2">This batch was archived.</Typography>;
    }
    return orderedRows.map((row) => (
      <SmartUploadReviewRow
        key={row.id}
        row={row}
        lines={linesByRow[row.id] || []}
        companyId={companyId}
        taskDocId={taskDocId}
        entityNames={entityNames}
        vendorOptions={vendorOptions}
        fixedVendorName={fixedVendorName}
        closedYears={closedYears}
        commitResults={commitResults}
        isCommitting={isCommitting}
        onPendingChange={onPendingChange}
        onConfirmLines={requestCommit}
        onRetryFiling={retryFiling}
        onDismissRow={dismissRow}
        onViewSource={(sourceRow, line) => setSourceView({ row: sourceRow, line })}
        onOpenManualEntry={requestManualEntry}
        isViewOnly={reviewMode !== "review"}
        // append-manual-line (unlike commit-lines/update-lines) also accepts
        // a finished task -- #42's spec has a finished batch's error/no-
        // activity rows still reachable through "Continue with guided
        // manual entry" (Verification Step 11 step (d)). Only "reading"
        // (extraction still running, nothing to act on yet) blocks it.
        canAppendManually={reviewMode !== "reading"}
      />
    ));
  };

  const isReviewable = !archived && reviewMode === "review";
  const isConfirmAllDisabled =
    !readyEntries.length || !!blockingReasons.length || isCommitting;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">Review uploaded documents</Typography>
            <Typography variant="caption" color="textSecondary">
              {batchSummary({ rows, entries })}
            </Typography>
          </Box>
          <IconButton aria-label="Close review" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {!archived && REVIEW_MODE_NOTICES[reviewMode] && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {REVIEW_MODE_NOTICES[reviewMode]}
          </Alert>
        )}
        {isPhysicalContext && isReviewable && (
          <Typography variant="body2" color="textSecondary">
            These documents were uploaded to {contextEntityName}.
          </Typography>
        )}
        {commitProgress && (
          <Box sx={{ my: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Recording {commitProgress.done} of {commitProgress.total}{" "}
              {commitProgress.total === 1 ? "line" : "lines"}…
            </Typography>
            <LinearProgress
              variant="determinate"
              value={
                commitProgress.total
                  ? (commitProgress.done / commitProgress.total) * 100
                  : 0
              }
            />
          </Box>
        )}
        <Box sx={{ display: "flex", mt: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0, overflowX: "auto" }}>
            {renderBody()}
          </Box>
          {/* This dialog is a read-only document viewer with no in-progress
              transaction to attach an annotation to, unlike
              SmartUploadManualEntryDialog -- so the snip-and-comment tool
              itself is disabled here rather than offering a "Save comment"
              flow that has nothing to persist to. */}
          {sourceView && (
            <SmartUploadSourcePanel
              key={sourceView.row.id}
              row={sourceView.row}
              line={sourceView.line}
              onClose={() => setSourceView(null)}
              allowAnnotation={false}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ gap: 1 }}>
        {isReviewable && !!blockingReasons.length && (
          <Typography variant="caption" color="textSecondary">
            {blockingReasons.join(" · ")}
          </Typography>
        )}
        <Button onClick={onClose}>Close</Button>
        {isReviewable && (
          <Button
            variant="contained"
            color="secondary"
            disabled={isConfirmAllDisabled}
            sx={{ color: palette.common.white }}
            onClick={() => requestCommit(readyEntries)}
          >
            Confirm all ({readyEntries.length} ready)
          </Button>
        )}
      </DialogActions>
      {promptEntries && (
        <SmartUploadSlideoutPrompt
          entityName={contextEntityName}
          onCancel={() => setPromptEntries(null)}
          onConfirm={confirmSlideoutPrompt}
        />
      )}
      {manualEntryTarget && (
        <SmartUploadManualEntryDialog
          companyId={companyId}
          task={{ ...task, id: taskDocId }}
          row={manualEntryTarget.row}
          line={manualEntryTarget.line}
          onClose={() => setManualEntryTarget(null)}
        />
      )}
    </Dialog>
  );
};

export default SmartUploadReviewDialog;
